import { datadogRum } from '@datadog/browser-rum'
import { Box, Paper, Stack, Typography } from '@mui/material'
import Image from 'next/image'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useIsMobileWidth } from '@omnea-app/fe-browser-utils/useDetectDevice'
import { StatusIndicator } from '@omnea-app/frontend/components/shared/ui/status-indicator/StatusIndicator'
import { sourceSans3 } from '@omnea-app/frontend/config/fonts'
import { useSystemStatus } from '@omnea-app/frontend/utils/hooks/useSystemStatus'
import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'
import { LoginEmailContainer } from './LoginEmailContainer'
import { LoginMagicLinkContainer } from './LoginMagicLinkContainer'

export type LoginSteps = 'Initial' | 'MagicLink'

export interface Props {
  signupError: string | string[] | null
  onUserInputChange: () => void
  emailFromUrl: string | string[] | undefined | null
  onInitialLoginRequest: ({ email, useEmailFlow }: { email: string; useEmailFlow?: boolean }) => Promise<void>
  loginStep: LoginSteps
  setLoginStep: Dispatch<SetStateAction<LoginSteps>>
}

export const OmneaSideBar = () => {
  const { t } = useTranslation('login')
  return (
    <Stack
      sx={{
        maxWidth: '700px',
        paddingX: 10,
        padding: 16,
        margin: '0 auto',
        backgroundImage: 'url(/app/assets/login-background.svg)',
        backgroundClip: 'border-box',
        backgroundSize: 'cover',
        overflow: 'hidden',
        justifyContent: 'space-between',
      }}
    >
      <Image src="/logo.png" alt="Omnea" height={26} width={143} />
      <Box sx={{ position: 'relative', flexGrow: 1, maxHeight: 450 }}>
        <Image src="/app/assets/login.svg" alt="Omnea Login" fill />
      </Box>
      <Stack spacing={4}>
        <Typography
          className={sourceSans3.variable}
          suppressHydrationWarning
          variant="display-h1"
          component="span"
          // oxlint-disable-next-line omnea-internal/no-restricted-syntax -- banner on login page, text over an image
          color="rgba(239, 241, 244, 0.65)"
          // FIXME: no theme2 variant matches this bespoke marketing size/weight — replace with a real variant once one exists (was Tagline)
          sx={{ fontSize: '2.25rem', fontWeight: 600, lineHeight: '43px' }}
        >
          {t('tagline')}
        </Typography>
        <Typography
          suppressHydrationWarning
          variant="upsized-regular"
          // oxlint-disable-next-line omnea-internal/no-restricted-syntax -- banner on login page, text over an image
          color="#EFF1F4"
          sx={{ /* FIXME: use a supported typography variant */ fontSize: '1.125rem', lineHeight: '27px' }}
        >
          {t('tagline-sub')}
        </Typography>
      </Stack>
    </Stack>
  )
}

export const LoginPage = ({
  signupError,
  onUserInputChange,
  emailFromUrl,
  onInitialLoginRequest,
  loginStep,
  setLoginStep,
}: Props) => {
  const { t } = useTranslation('login')
  const isMobile = useIsMobileWidth()
  const { data: systemStatus } = useSystemStatus()

  const defaultEmail = typeof emailFromUrl === 'string' ? emailFromUrl : emailFromUrl?.[0]
  const [email, setEmail] = useState(defaultEmail ?? '')

  useEffect(() => {
    if (defaultEmail) setEmail(defaultEmail)
  }, [defaultEmail])

  const getNextAuthError = (error?: string | string[] | null): string | null => {
    const err = typeof error === 'string' ? error : error?.[0]
    if (!err) return null
    switch (err) {
      case 'Default':
        return t('validation.default-error')
      case 'CredentialsSignin':
        return t('validation.invalid-credentials')
      case 'MagicLink':
        return t('validation.invalid-link')
      case 'Callback':
        datadogRum.addError(new Error('SSO callback failed'), { error })
        return t('validation.sso-error')
      default: {
        datadogRum.addError(new Error('SSO callback failed'), { error })
        if (err.startsWith('OAuth')) {
          return t('validation.sso-error')
        }
        return null
      }
    }
  }

  const nextAuthError = getNextAuthError(signupError)

  const handleEmailChange = (newEmail: string) => {
    setEmail(newEmail)
    onUserInputChange()
  }

  const handleBackClick = () => {
    setLoginStep('Initial')
    setEmail('')
  }

  const getLoginStep = (step: LoginSteps) => {
    switch (step) {
      case 'Initial':
        return (
          <LoginEmailContainer
            email={email}
            onEmailChange={handleEmailChange}
            onSubmit={() => onInitialLoginRequest({ email })}
            credentialsError={nextAuthError}
          />
        )
      case 'MagicLink':
        return <LoginMagicLinkContainer email={email} onBackClicked={handleBackClick} />
      default:
        assertNever(step)
    }
  }

  return (
    <Stack direction="row" sx={{ flexGrow: 1 }}>
      {!isMobile && <OmneaSideBar />}
      <Stack direction="row" style={{ flexGrow: 1, margin: '0 auto' }} alignItems="center">
        <Box sx={{ minWidth: 'min(440px, 100%)', mx: 'auto', width: '100%', p: { xs: 4, md: 10 } }}>
          <Paper sx={{ p: { xs: 4, md: 10 }, maxWidth: 440, mx: 'auto', width: '100%' }}>{getLoginStep(loginStep)}</Paper>
        </Box>
      </Stack>

      {systemStatus && (
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 16, md: 24 },
            right: { xs: 16, md: 24 },
          }}
        >
          <StatusIndicator
            indicator={systemStatus.indicator}
            description={systemStatus.description}
            statusPageUrl={systemStatus.pageUrl}
            ariaLabel={t('status-indicator.aria-label', { description: systemStatus.description })}
          />
        </Box>
      )}
    </Stack>
  )
}
